/**
 * TeamDetailPage (AC-891)
 *
 * Displays the detail view for a single team: header with name/description,
 * members list with role badges and action buttons, shared projects feed,
 * and owner-only management actions (invite, delete).
 *
 * Route: /teams/:teamId
 * Parent: Layout via Outlet
 * User Access: Click team card on MyTeamsPage -> navigates here
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Users, ArrowLeft, UserPlus, Trash2 } from 'lucide-react';
import { useTeamStore } from '../store/teamStore';
import { useAuthStore } from '../store';
import ProjectCard from '../components/social/ProjectCard';
import TeamMemberList from '../components/teams/TeamMemberList';
import TeamInviteDialog from '../components/teams/TeamInviteDialog';
import { useContextualNavigation } from '../hooks/useContextualNavigation';

const TeamDetailPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { navigateWithContext } = useContextualNavigation();
  const { user } = useAuthStore();
  const {
    currentTeam,
    teamMembers,
    teamProjects,
    loading,
    loadTeamDetail,
    loadTeamMembers,
    loadTeamProjects,
    removeTeamMember,
    deleteTeam,
  } = useTeamStore();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const numericTeamId = teamId ? Number(teamId) : 0;

  useEffect(() => {
    if (numericTeamId) {
      loadTeamDetail(numericTeamId);
      loadTeamMembers(numericTeamId);
      loadTeamProjects(numericTeamId);
    }
  }, [numericTeamId, loadTeamDetail, loadTeamMembers, loadTeamProjects]);

  const currentUserId = user ? Number(user.id) : 0;
  const isOwner = currentTeam?.is_owner ?? false;

  const handleRemoveMember = async (userId: number) => {
    await removeTeamMember(numericTeamId, userId);
  };

  const handleLeaveTeam = async () => {
    await removeTeamMember(numericTeamId, currentUserId);
    navigate('/teams');
  };

  const handleDeleteTeam = async () => {
    await deleteTeam(numericTeamId);
    navigate('/teams');
  };

  const handleProjectClick = (projectId: number) => {
    navigateWithContext(`/shared/projects/${projectId}`, currentTeam?.name || 'Team');
  };

  if (loading && !currentTeam) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Loading team...</p>
        </div>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Team not found.</p>
          <button
            onClick={() => navigate('/teams')}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Back to Teams
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Back navigation */}
      <button
        onClick={() => navigate('/teams')}
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-4 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" aria-hidden="true" />
        Back to Teams
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-8 gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" aria-hidden="true" />
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {currentTeam.name}
            </h1>
          </div>
          {currentTeam.description && (
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {currentTeam.description}
            </p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {currentTeam.member_count} {currentTeam.member_count === 1 ? 'member' : 'members'}
          </p>
        </div>

        {/* Owner actions */}
        {isOwner && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowInviteDialog(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
            >
              <UserPlus className="w-4 h-4" aria-hidden="true" />
              Invite Member
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
              Delete Team
            </button>
          </div>
        )}
      </div>

      {/* Members Section */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Members
        </h2>
        {teamMembers.length > 0 ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <TeamMemberList
              members={teamMembers}
              currentUserId={currentUserId}
              isOwner={isOwner}
              onRemoveMember={handleRemoveMember}
              onLeaveTeam={handleLeaveTeam}
            />
          </div>
        ) : (
          <p className="text-gray-500 dark:text-gray-400">No members found.</p>
        )}
      </section>

      {/* Shared Projects Section */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Shared Projects
        </h2>
        {teamProjects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {teamProjects.map((project) => (
              <ProjectCard
                key={project.project_id}
                project={project}
                onClick={handleProjectClick}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" aria-hidden="true" />
            <p className="text-gray-500 dark:text-gray-400">
              No projects shared with this team yet.
            </p>
          </div>
        )}
      </section>

      {/* Invite Member Dialog */}
      <TeamInviteDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        teamId={numericTeamId}
      />

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-team-title"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3
              id="delete-team-title"
              className="text-lg font-semibold text-gray-900 dark:text-white mb-2"
            >
              Delete Team
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete &quot;{currentTeam.name}&quot;? This action cannot be undone.
              All members will be removed.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTeam}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetailPage;
